import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table, Button, Descriptions, Switch, Flex } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import {
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Context for providing DnD handle
const RowContext = React.createContext({});

// Drag handle component
const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      icon={<HolderOutlined />}
      style={{ cursor: "grab", color: "#999" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

// Custom table row with drag props
const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 999, position: "relative" } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const Rearrangement = ({ data, onUpdate, baseData }) => {
  const [dataSource, setDataSource] = useState([]);

  const [logoOption, setLogoOption] = useState(
    baseData?.use_custom ? "custom" : "default"
  );

  useEffect(() => {
    setDataSource(data);
    setLogoOption(baseData?.use_custom ? "custom" : "default");
  }, [data, baseData]);

  const sensors = useSensors(useSensor(PointerSensor));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = dataSource.findIndex(
      (item) => item.name_slug === active.id
    );
    const newIndex = dataSource.findIndex((item) => item.name_slug === over.id);

    const newData = arrayMove(dataSource, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        custom_order: index + 1,
      })
    );

    setDataSource(newData);
  };

  const columns = [
    {
      title: "",
      dataIndex: "sort",
      width: 50,
      align: "center",
      render: () => <DragHandle />,
    },
    {
      title: "Sections",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Default Order",
      dataIndex: "default_order",
      key: "default_order",
    },
    {
      title: "Custom Order",
      dataIndex: "custom_order",
      key: "custom_order",
    },
  ];

  const handleLogoChange = (e) => {
    setLogoOption(e);
  };

  const handleSave = () => {
    let payload = {
      id: baseData?.id,
      use_custom: logoOption === "custom" ? true : false,
      value_json: dataSource,
    };

    onUpdate(payload);
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={dataSource.map((item) => item.name_slug)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="name_slug"
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            components={{
              body: {
                row: Row,
              },
            }}
          />
        </SortableContext>
      </DndContext>

      <Descriptions
        column={2}
        bordered
        className="mt-4"
        labelStyle={{ width: "10%" }}
        contentStyle={{ width: "90%" }}
      >
        <Descriptions.Item label="Use Custom">
          <Switch
            checked={logoOption === "custom"}
            onChange={(checked) =>
              handleLogoChange(checked ? "custom" : "default")
            }
            checkedChildren="Custom"
            unCheckedChildren="Default"
          />
        </Descriptions.Item>
      </Descriptions>

      <Flex className="mt-5" justify="flex-end">
        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </div>
  );
};

export default Rearrangement;
